import type { DocumentPageMetadataUserEditStatus } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { EvalPagePool } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalPool'
import {
  appendUniqueEvalPages,
  EVAL_QUEUE_LOOKAHEAD,
  evalQueueExcludePageIds,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalQueue'
import type { MetadataEvalSampledPage } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalSampling'
import {
  EMPTY_EVAL_PAGE_TRAIL,
  evalQueueNavEnabled,
  evalTrailAppend,
  evalTrailDecisionForPage,
  evalTrailMarkCursor,
  evalTrailPageAtCursor,
  evalTrailStepBack,
  evalTrailStepForwardVisited,
  evalTrailVisitedPageIds,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalTrail'
import {
  loadEvalSessionState,
  saveEvalSessionState,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalTrailStorage'
import { useCallback, useEffect, useRef, useState } from 'react'

export type EvalPageSampler = (
  limit: number,
  excludePageIds: string[]
) => Promise<MetadataEvalSampledPage[]>

export function useEvalPageQueue({
  enabled,
  scopeKey,
  pool,
  samplePages,
  snapshot = false,
}: {
  enabled: boolean
  scopeKey: string
  pool: EvalPagePool
  samplePages: EvalPageSampler
  snapshot?: boolean
}) {
  const [loadedScope, setLoadedScope] = useState(scopeKey)
  const alive = useRef(false)
  const [trail, setTrail] = useState(EMPTY_EVAL_PAGE_TRAIL)
  const [queuedPages, setQueuedPages] = useState<MetadataEvalSampledPage[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSampling, setIsSampling] = useState(false)
  const [error, setError] = useState<Error>()
  const trailRef = useRef(trail)
  const queueRef = useRef(queuedPages)
  const morePagesRemain = useRef(true)
  const generation = useRef(0)
  const sampler = useRef(samplePages)
  const scope = useRef(scopeKey)
  const pending = useRef<Promise<boolean>>()
  sampler.current = samplePages
  scope.current = scopeKey

  const persist = useCallback(() => {
    // A disabled render (e.g. still waiting on pipeline-config/user email
    // right after mount) resets this hook's in-memory state to empty, but
    // must not persist that — it would delete a real trail a prior enabled
    // session saved for this same scope, breaking restore-on-remount.
    if (!alive.current) return
    saveEvalSessionState(scope.current, {
      trail: trailRef.current,
      queuedPages: queueRef.current,
      snapshotComplete: snapshot && !morePagesRemain.current,
    })
  }, [snapshot])
  const commitTrail = useCallback(
    (next: typeof trail) => {
      trailRef.current = next
      setTrail(next)
      persist()
    },
    [persist]
  )
  const commitQueue = useCallback(
    (next: MetadataEvalSampledPage[]) => {
      queueRef.current = next
      setQueuedPages(next)
      persist()
    },
    [persist]
  )

  const fill = useCallback((): Promise<boolean> => {
    if (!alive.current) return Promise.resolve(false)
    if (!morePagesRemain.current) {
      setIsLoading(false)
      setIsSampling(false)
      return Promise.resolve(true)
    }
    if (pending.current) return pending.current
    const initial = trailRef.current.visited.length === 0
    const need =
      EVAL_QUEUE_LOOKAHEAD + (initial ? 1 : 0) - queueRef.current.length
    if (need <= 0) {
      setIsLoading(false)
      setIsSampling(false)
      return Promise.resolve(true)
    }
    const requestGeneration = generation.current
    const requestScope = scope.current
    const isCurrent = () =>
      requestGeneration === generation.current && requestScope === scope.current
    if (initial) {
      setIsLoading(true)
      setIsSampling(true)
    }
    setError(undefined)
    const request = (async () => {
      try {
        const sample = sampler.current
        const exclusions = evalQueueExcludePageIds(
          queueRef.current,
          evalTrailVisitedPageIds(trailRef.current)
        )
        const pages = await Promise.resolve().then(() =>
          sample(need, exclusions)
        )
        if (!isCurrent()) return false
        if (snapshot) {
          morePagesRemain.current = false
        }
        const visited = new Set(evalTrailVisitedPageIds(trailRef.current))
        const next = appendUniqueEvalPages(
          queueRef.current,
          pages.filter((page) => !visited.has(page.page_id))
        )
        if (initial && next[0]) {
          commitTrail(
            evalTrailAppend(
              EMPTY_EVAL_PAGE_TRAIL,
              next[0],
              evalTrailDecisionForPage(pool, next[0])
            )
          )
          commitQueue(next.slice(1))
        } else {
          commitQueue(next)
        }
        return true
      } catch (cause) {
        if (isCurrent())
          setError(
            cause instanceof Error
              ? cause
              : new Error('Next-page sampling failed')
          )
        return false
      } finally {
        if (isCurrent()) {
          pending.current = undefined
          setIsLoading(false)
          setIsSampling(false)
        }
      }
    })()
    pending.current = request
    return request
  }, [commitQueue, commitTrail, pool, snapshot])

  useEffect(() => {
    alive.current = enabled
    setLoadedScope(scopeKey)
    generation.current += 1
    pending.current = undefined
    const restored = enabled ? loadEvalSessionState(scopeKey) : undefined
    morePagesRemain.current = !(snapshot && restored?.snapshotComplete)
    commitTrail(restored?.trail ?? EMPTY_EVAL_PAGE_TRAIL)
    commitQueue(restored?.queuedPages ?? [])
    setError(undefined)
    setIsLoading(true)
    setIsSampling(false)
    if (enabled) void fill()
    return () => {
      alive.current = false
      generation.current += 1
      pending.current = undefined
    }
  }, [commitQueue, commitTrail, enabled, fill, scopeKey, snapshot])

  const activeGeneration = generation.current
  const advance = useCallback(async (): Promise<
    'advanced' | 'empty' | 'failed'
  > => {
    if (
      !alive.current ||
      scope.current !== scopeKey ||
      generation.current !== activeGeneration
    )
      return 'failed'
    const current = evalTrailPageAtCursor(trailRef.current)
    if (
      !current ||
      (pool === 'not_labeled_by_me' && current.decision === 'unlabeled')
    )
      return 'failed'
    const requestGeneration = generation.current
    const requestScope = scope.current
    const stepped = evalTrailStepForwardVisited(trailRef.current)
    if (stepped) {
      commitTrail(stepped)
      return 'advanced'
    }
    if (queueRef.current.length === 0) {
      if (!morePagesRemain.current) return 'empty'
      setIsSampling(true)
      if (!(await fill())) return 'failed'
    }
    if (
      generation.current !== requestGeneration ||
      scope.current !== requestScope
    )
      return 'failed'
    const next = queueRef.current[0]
    if (!next) return 'empty'
    commitQueue(queueRef.current.slice(1))
    commitTrail(
      evalTrailAppend(
        trailRef.current,
        next,
        evalTrailDecisionForPage(pool, next)
      )
    )
    if (morePagesRemain.current) void fill()
    return 'advanced'
  }, [activeGeneration, commitQueue, commitTrail, fill, pool, scopeKey])

  const previous = useCallback(() => {
    if (
      !alive.current ||
      scope.current !== scopeKey ||
      generation.current !== activeGeneration
    )
      return
    const stepped = evalTrailStepBack(trailRef.current)
    if (stepped) commitTrail(stepped)
  }, [activeGeneration, commitTrail, scopeKey])

  const markDecision = useCallback(
    (pageId: string, decision: DocumentPageMetadataUserEditStatus) => {
      if (
        !alive.current ||
        scope.current !== scopeKey ||
        generation.current !== activeGeneration ||
        evalTrailPageAtCursor(trailRef.current)?.page_id !== pageId
      )
        return false
      commitTrail(evalTrailMarkCursor(trailRef.current, decision))
      return true
    },
    [activeGeneration, commitTrail, scopeKey]
  )

  const isCurrentScope = enabled && loadedScope === scopeKey
  const currentPage = isCurrentScope ? evalTrailPageAtCursor(trail) : undefined
  const nav = evalQueueNavEnabled({
    trail,
    upcomingCount: queuedPages.length,
    pool,
  })
  return {
    currentPage,
    trail: isCurrentScope ? trail : EMPTY_EVAL_PAGE_TRAIL,
    queuedPages: isCurrentScope ? queuedPages : [],
    isLoading,
    isSampling,
    error,
    ...nav,
    advance,
    previous,
    markDecision,
    retry: fill,
  }
}
